import { useState, useEffect } from 'react';
import type { AnnouncementSettings, WhatsAppSettings } from '../../types';
import type { GlobalPixelSettings } from '../../types';
import type { WebsiteSettings } from './types';
import {
    normalizeAnnouncementSettings,
    defaultAnnouncementSettings,
    normalizeWhatsappSettings,
    defaultWhatsappSettings,
} from './normalize';
import { applyWebsiteMeta } from './applyDynamicMeta';
import { maybeInjectGoogleAnalytics } from './injectGoogleAnalytics';
import { onIdle } from '../../utils/idleCallback';
import { getPublicTrackingPixels } from '../../utils/cacheHelpers/public';

export function useSettingsState() {
    const [websiteSettings, setWebsiteSettings] = useState<WebsiteSettings | null>(null);
    const [trackingSettings, setTrackingSettings] = useState<GlobalPixelSettings | null>(null);
    const [announcementSettings, setAnnouncementSettings] = useState<AnnouncementSettings | null>(null);
    const [whatsappSettings, setWhatsappSettings] = useState<WhatsAppSettings | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGlobalSettings = async () => {
            try {
                // Dynamic import: fetch settings jalan di idle (rute publik) — jangan
                // tarik supabase-js (±165KB) ke entry kritis. Modul di-cache runtime.
                const { supabase } = await import('../../supabase/client');
                // trackingPixels TIDAK lagi ikut select('*') langsung — row itu berisi
                // CAPI access token (rahasia). Browser hanya butuh pixelId untuk init
                // script (fbq/gtag/ttq), diambil lewat RPC id-only (fix kebocoran CAPI
                // token 2026-08-23). Query lain tetap satu roundtrip. Pixel ID lewat
                // helper bersama (memo in-flight 120 dtk) — LandingPageViewer
                // memanggil RPC yang SAMA; tanpa ini /lp menembak 2x (2026-10-22).
                const [{ data: allSettings }, { data: pixelIds }] = await Promise.all([
                    supabase.from('settings').select('*').in('id', ['website', 'announcementSettings', 'whatsapp']),
                    getPublicTrackingPixels(),
                ]);
                const findSetting = (id: string) => allSettings?.find(s => s.id === id) || null;
                let websiteData = findSetting('website');
                const trackingData = pixelIds && typeof pixelIds === 'object' ? { id: 'trackingPixels', ...(pixelIds as Record<string, unknown>) } : null;
                const announcementData = findSetting('announcementSettings');
                const whatsappData = findSetting('whatsapp');

                // Anon (halaman publik): RLS mengecualikan baris id='website' (berisi secret di
                // website_settings), jadi ambil meta publik aman via RPC SECURITY DEFINER.
                if (!websiteData) {
                    try {
                        const { data: publicMeta } = await supabase.rpc('get_public_website_meta');
                        // RPC returns TABLE → data adalah array of rows; ambil baris pertama.
                        const metaRow = Array.isArray(publicMeta) ? publicMeta[0] : publicMeta;
                        if (metaRow) websiteData = metaRow as typeof websiteData;
                    } catch (e) {
                        console.error("Failed to fetch public website meta:", e);
                    }
                }

                if (websiteData) {
                    setWebsiteSettings(websiteData as WebsiteSettings);
                    applyWebsiteMeta(websiteData as WebsiteSettings);
                    // Pixel di AKHIR (2026-10-19): gtag jangan berebut bandwidth LCP.
                    const gaId = (websiteData as WebsiteSettings).googleAnalyticsId;
                    if (gaId) onIdle(() => maybeInjectGoogleAnalytics(gaId), 3000);
                }

                if (trackingData) {
                    setTrackingSettings(trackingData as unknown as GlobalPixelSettings);
                }

                if (announcementData) {
                    setAnnouncementSettings(normalizeAnnouncementSettings(announcementData));
                } else {
                    // Set defaults if not found
                    setAnnouncementSettings(defaultAnnouncementSettings());
                }

                // Load WhatsApp settings
                if (whatsappData) {
                    setWhatsappSettings(normalizeWhatsappSettings(whatsappData));
                } else {
                    // Set defaults
                    setWhatsappSettings(defaultWhatsappSettings());
                }

            } catch (error) {
                console.error("Failed to fetch global settings:", error);
                // Set defaults on error
                setAnnouncementSettings(defaultAnnouncementSettings());
                setWhatsappSettings(defaultWhatsappSettings());
            } finally {
                setLoading(false);
            }
        };

        // Rute publik (/lp/ /f/ /d/ /ai-lp/) tidak butuh settings saat
        // first-paint — tunda fetch agar tak berebut koneksi/CPU dengan query
        // data produk + LCP (perf 2026-10-11; sebelumnya hanya /lp/ yg ditunda,
        // /f/ ikut balapan → form terasa lama kebuka di HP).
        if (typeof window !== 'undefined') {
            const p = window.location.pathname;
            if (p.startsWith('/lp/') || p.startsWith('/f/')
                || p.startsWith('/d/') || p.startsWith('/ai-lp/')) {
                return onIdle(fetchGlobalSettings, 1500);
            }
        }
        fetchGlobalSettings();
    }, []);

    return { websiteSettings, trackingSettings, announcementSettings, whatsappSettings, loading };
}
